import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_STORAGE_CAPABILITIES_QUERY_KEY = 'cluster-storage-capabilities';

type Params = {
  organizationId: string;
  clusterId?: string;
  enabled?: boolean;
};

const getClusterStorageCapabilitiesQueryKey = ({
  organizationId,
  clusterId,
}: Partial<Omit<Params, 'enabled'>> = {}) =>
  [CLUSTER_STORAGE_CAPABILITIES_QUERY_KEY, organizationId, clusterId] as const;

const getClusterStorageCapabilitiesQueryOptions = ({
  organizationId,
  clusterId,
  enabled = true,
}: Params) =>
  queryOptions({
    queryKey: getClusterStorageCapabilitiesQueryKey({
      organizationId,
      clusterId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getClusterStorageSummary({
        organizationId,
        clusterId: clusterId!,
      });

      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, enabled),
  });

type UseClusterStorageCapabilitiesQueryArgs = Omit<Params, 'organizationId'>;

export const useClusterStorageCapabilitiesQuery = ({
  clusterId,
  enabled = true,
}: UseClusterStorageCapabilitiesQueryArgs = {}) => {
  const organizationId = useCurrentOrganizationId();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getClusterStorageCapabilitiesQueryOptions({
      organizationId,
      clusterId,
      enabled,
    })
  );

  return {
    storageCapabilities: data?.capabilities,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
